import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import type { Role } from '@lov/shared';

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles: Role[];
  fallbackPath?: string;
}

/**
 * RoleProtectedRoute - Protects routes based on authentication AND user role
 *
 * @param children - The component to render if authorized
 * @param requiredRoles - Array of roles that are allowed to access this route
 * @param fallbackPath - Where to redirect if role doesn't match (default: /dashboard)
 */
export function RoleProtectedRoute({
  children,
  requiredRoles,
  fallbackPath = '/dashboard'
}: RoleProtectedRouteProps) {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role authorization
  if (!user || !requiredRoles.includes(user.role)) {
    // User is authenticated but doesn't have required role
    // Redirect to fallback (dashboard) instead of login
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
}
